import { Checkbox } from '@material-ui/core';
import classNames from 'classnames';
import { findIndex, isArray, orderBy, some } from 'lodash';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { AutoSizer, Column, SortDirection, SortDirectionType, Table } from 'react-virtualized';
import { Maybe, Nullable } from '../../Shared/Types';
import { ArrowSortDownIcon, ArrowSortUpIcon } from '../Icons';
import styles from './SimpleDataTable.module.scss';

type sortFunctionType<T> = (currentRow: T) => number | string | Date;

export interface IColumnInfo<T> {
    key: string;
    title: string;
    width?: number;
    minWidth?: number;
    grow?: number;
    sort?: (string | sortFunctionType<T>)[];
    render?(row: T): React.ReactNode;
}

type rowFunction<T> = (row: T, rowIndex: number) => React.ReactNode;

type CellType<CellData> = {
    cellData?: unknown;
    columnIndex?: number;
    dataKey: string;
    isScrolling?: boolean;
    rowData: CellData;
    rowIndex?: number;
};

type SimpleDataTableProps<CellData extends object> = {
    enableSelection?: boolean;
    enableSelectAll?: boolean;
    enableScrollTo?: boolean;
    multiSelect?: boolean;
    selectedRows?: CellData[];
    data: CellData[];
    columns: IColumnInfo<CellData>[];
    defaultSort?: keyof CellData;
    menu?: false | rowFunction<CellData>;
    showBorder?: boolean;
    disableHeader?: boolean;
    reduceRowMargins?: boolean;
    noResultText?: string;
    onRowSelection?(rows: CellData[]): void;
    rowComparer?(rowA: CellData, rowB: CellData): boolean;
};

@observer
export class SimpleDataTable<CellData extends object> extends React.Component<SimpleDataTableProps<CellData>> {
    @observable public sortDirection: SortDirectionType = SortDirection.ASC;
    @observable public sortBy: Maybe<keyof CellData>;
    public rowComparer = this.props.rowComparer ? this.props.rowComparer : (a: CellData, b: CellData) => a === b;

    constructor(props: SimpleDataTableProps<CellData>, context: any) {
        super(props, context);
        this.sortBy = props.defaultSort;
    }

    @action
    public sortFunction = (sort: { sortBy: string; sortDirection: SortDirectionType }): void => {
        this.sortBy = sort.sortBy as keyof CellData;
        this.sortDirection = sort.sortDirection;
    };

    @computed
    get sortedData(): CellData[] {
        const targetColumn = this.props.columns.find(column => column.key === this.sortBy);

        const collection: CellData[] = this.props.data;

        if (targetColumn && targetColumn.sort) {
            const sortColumns = targetColumn.sort;

            if (isArray(sortColumns)) {
                return orderBy(
                    collection,
                    sortColumns,
                    sortColumns.map(() => (this.sortDirection.toLowerCase() === 'asc' ? 'asc' : 'desc'))
                );
            } else return orderBy(collection, sortColumns);
        } else if (this.sortBy) {
            return orderBy(collection, this.sortBy, this.sortDirection.toLowerCase() === 'asc' ? 'asc' : 'desc');
        } else {
            return collection;
        }
    }

    public rowGetter = (row: { index: number }) => {
        return this.sortedData[row.index];
    };

    public checkboxColumnRenderer = (cell: CellType<CellData>): React.ReactNode => {
        return <Checkbox checked={this.isRowChosen(cell.rowData)} />;
    };

    public checkboxColumnHeaderRenderer = (): React.ReactNode => {
        if (!this.props.enableSelectAll) return;

        const allItemsChosen = this.props.selectedRows
            ? this.props.selectedRows.length === this.props.data.length
            : false;
        return (
            <Checkbox
                checked={allItemsChosen}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (this.props.onRowSelection) {
                        if (e.target.checked) this.props.onRowSelection(this.props.data);
                        else this.props.onRowSelection([]);
                    }
                }}
            />
        );
    };

    public menuRenderer = (cell: CellType<CellData>): React.ReactNode => {
        if (this.props.menu && typeof cell.rowIndex !== 'undefined') {
            return this.props.menu(cell.rowData, cell.rowIndex);
        }

        return;
    };

    public cellRenderer = (cell: CellType<CellData>): React.ReactNode => {
        if (typeof cell.columnIndex === 'undefined') return '';

        if (typeof cell.dataKey === 'undefined') return '';

        const columnIndex =
            this.props.enableSelection || this.props.enableSelectAll ? cell.columnIndex - 1 : cell.columnIndex;
        const column = this.props.columns[columnIndex];

        if (column && column.render) return column.render(cell.rowData);

        if (cell && cell.rowData && cell.rowData.hasOwnProperty(cell.dataKey))
            return cell.rowData[cell.dataKey as keyof CellData];

        return '';
    };

    public columnHeaderRender = (header: {
        dataKey?: string;
        disableSort?: boolean;
        label?: any;
        sortBy?: string;
        sortDirection?: SortDirectionType;
    }): React.ReactNode => {
        let sortIcon: JSX.Element | null = null;

        if (header.sortBy === header.dataKey) {
            if (header.sortDirection === SortDirection.ASC) sortIcon = <ArrowSortUpIcon />;
            else sortIcon = <ArrowSortDownIcon />;
        }

        const headerStyles = classNames({
            [styles.headerLabel]: true,
            [styles.sortedBy]: header.sortBy === header.dataKey
        });

        return (
            <div className={styles.headerContainer}>
                <span className={headerStyles} title={header.label}>
                    {header.label}
                </span>
                {sortIcon}
            </div>
        );
    };

    public onRowClick = (event: { event: React.MouseEvent; index: number; rowData: any }) => {
        if (!this.props.onRowSelection) return;

        if (!this.props.enableSelection) {
            return this.props.onRowSelection([event.rowData]);
        }

        const selectedRows = this.props.selectedRows ? this.props.selectedRows : [];

        if (this.isRowChosen(event.rowData)) {
            if (this.props.multiSelect) {
                const indexToRemove = selectedRows.indexOf(event.rowData);
                this.props.onRowSelection([
                    ...selectedRows.slice(0, indexToRemove),
                    ...selectedRows.slice(indexToRemove + 1)
                ]);
            } else {
                this.props.onRowSelection([]);
            }
        } else {
            if (this.props.multiSelect) {
                const newSelectedRows = [...selectedRows, event.rowData];

                this.props.onRowSelection(newSelectedRows);
            } else {
                this.props.onRowSelection([event.rowData]);
            }
        }
    };

    public isRowChosen(rowData: any): boolean {
        if (!this.props.selectedRows) return false;

        return !!this.props.selectedRows.find(
            selectedRow => !!selectedRow && !!rowData && this.rowComparer(selectedRow, rowData)
        );
    }

    public rowClassName = (row: { index: number }): string => {
        return classNames({
            [styles.row]: true,
            [styles.rowActive]: this.isRowChosen(this.sortedData[row.index]),
            [styles.rowClickable]:
                this.props.enableSelectAll || this.props.enableSelection || this.props.onRowSelection,
            [styles.reduceRowMargins]: !!this.props.reduceRowMargins
        });
    };

    public noRowsRenderer = (): Nullable<JSX.Element> => {
        return <div className={styles.noResultText}>{this.props.noResultText || ''}</div>;
    };

    @computed
    get columns() {
        const showCheckBox = !!(this.props.enableSelectAll || this.props.enableSelection);
        const multiSelectEnabled = !!this.props.enableSelectAll;
        const checkBoxColumnEnabled = multiSelectEnabled || showCheckBox;

        const columns = this.props.columns.map(column => (
            <Column
                key={`column-${column.key}`}
                label={column.title || ''}
                disableSort={!column.sort}
                dataKey={column.key}
                cellRenderer={this.cellRenderer}
                headerRenderer={this.columnHeaderRender}
                flexGrow={column.grow !== undefined ? column.grow : column.width ? 0 : 1}
                minWidth={column.minWidth || 0}
                width={column.width || 50}
            />
        ));

        if (checkBoxColumnEnabled)
            columns.unshift(
                <Column
                    key='checkbox'
                    dataKey='checkbox'
                    width={40}
                    minWidth={40}
                    maxWidth={40}
                    flexShrink={0}
                    disableSort
                    cellRenderer={this.checkboxColumnRenderer}
                    headerRenderer={this.checkboxColumnHeaderRenderer}
                />
            );

        if (this.props.menu)
            columns.push(
                <Column
                    key='menu'
                    dataKey='menu'
                    width={40}
                    minWidth={40}
                    maxWidth={40}
                    flexShrink={0}
                    disableSort
                    cellRenderer={this.menuRenderer}
                />
            );

        return columns;
    }

    @computed get selectedRows() {
        if (!this.props.selectedRows) return [];

        // This also helps fix a weird MobX behavior
        return [...this.props.selectedRows];
    }

    public render() {
        let scrollToIndex: number;

        // This is split into two if's because MobX is too smart for it's own good.
        // It will stop observing values after a if statement if the if statement is false.
        if (this.selectedRows) {
            if (this.sortedData) {
                if (!this.props.multiSelect && this.props.enableScrollTo) {
                    scrollToIndex = findIndex(this.sortedData, (rowData: CellData) =>
                        some(this.selectedRows, selectedRow => this.rowComparer(selectedRow, rowData))
                    );
                }
            }
        }

        return (
            <AutoSizer
                children={({ height, width }) => {
                    return (
                        <Table
                            height={height}
                            width={width}
                            rowHeight={48}
                            headerHeight={50}
                            disableHeader={this.props.disableHeader}
                            sort={this.sortFunction}
                            sortBy={this.sortBy as string}
                            sortDirection={this.sortDirection}
                            scrollToIndex={scrollToIndex}
                            rowCount={this.props.data.length}
                            rowClassName={this.rowClassName}
                            onRowClick={this.onRowClick}
                            rowGetter={this.rowGetter}
                            noRowsRenderer={this.noRowsRenderer}
                            gridStyle={{ outline: 'none' }}
                        >
                            {this.columns}
                        </Table>
                    );
                }}
            />
        );
    }
}
